import { useEffect, useMemo, useState } from 'react';
import './ledger.css';
import ledgerService from '../../service/ledgerService';
import Loader from '../common/Loader';
import ConfirmModal from '../common/ConfirmModal';

export default function Ledger() {
  const user = useMemo(() => JSON.parse(localStorage.getItem('userDetails') || '{}'), []);

  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ win: 0, loss: 0, net: 0 });
  const [editingId, setEditingId] = useState(null);
  const [filters, setFilters] = useState({
    type: 'all',
    from: '',
    to: '',
    page: 1,
    limit: 20
  });
  const [form, setForm] = useState({
    entryDate: new Date().toISOString().slice(0, 10),
    type: 'win',
    amount: '',
    note: '',
  });
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: filters.limit,
      };
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const { data } = await ledgerService.getEntries(params);
      setEntries(data || []);
    } catch (err) {
      console.warn(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.type !== 'all') params.type = filters.type;
      const { data } = await ledgerService.getSummary(params);
      console.log({ data });
      const win = data?.totalWin || 0;
      const loss = data?.totalLoss || 0;
      setSummary({ win, loss, net: win - loss });
    } catch (err) {
      console.warn(err.message);
    }
  };

  useEffect(() => {
    loadEntries();
    loadSummary();
  }, [filters.page, filters.limit, filters.type, filters.from, filters.to]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value, page: 1 }));
  };

  const saveEntry = async () => {
    if (!form.entryDate || !form.amount || !form.type) return;
    try {
      if (editingId) {
        await ledgerService.updateEntry(editingId, {
          entryDate: form.entryDate,
          type: form.type,
          amount: Number(form.amount),
          note: form.note
        });
      } else {
        await ledgerService.addEntry({
          entryDate: form.entryDate,
          type: form.type,
          amount: Number(form.amount),
          note: form.note
        });
      }
      setForm((f) => ({ ...f, amount: '', note: '' }));
      setEditingId(null);
      loadEntries();
      loadSummary();
    } catch (err) {
      console.warn(err.message);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry._id || entry.id);
    const rawDate = entry.entryDate || entry.date || '';
    const dateVal = rawDate ? String(rawDate).slice(0, 10) : '';
    setForm({
      entryDate: dateVal,
      type: entry.type || 'win',
      amount: entry.amount != null ? String(entry.amount) : '',
      note: entry.note || ''
    });
  };

  const removeEntry = async (entry) => {
    setDeleteTarget(entry);
  };
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await ledgerService.deleteEntry(deleteTarget._id || deleteTarget.id);
      setDeleteTarget(null);
      loadEntries();
      loadSummary();
    } catch (err) {
      console.warn(err.message);
    }
  };
  const cancelDelete = () => setDeleteTarget(null);

  return (
    <div className="ledger-page">
      <div className="ledger-panel">
        <p className="eyebrow">Daily Ledger</p>
        <h2>Track wins & losses</h2>
        <div className="ledger-form">
          <div className="field">
            <label>Date</label>
            <input type="date" name="entryDate" value={form.entryDate} onChange={handleChange} />
          </div>
          <div className="field">
            <label>Type</label>
            <select name="type" value={form.type} onChange={handleChange}>
              <option value="win">Win</option>
              <option value="loss">Loss</option>
            </select>
          </div>
          <div className="field">
            <label>Amount</label>
            <input type="number" name="amount" value={form.amount} onChange={handleChange} placeholder="0" />
          </div>
          <div className="field">
            <label>Note</label>
            <input type="text" name="note" value={form.note} onChange={handleChange} placeholder="Optional" />
          </div>
          <button className="primary" onClick={saveEntry}>{editingId ? 'Update Entry' : 'Save Entry'}</button>
        </div>
      </div>

      <div className="ledger-panel stats">
        <h3>Totals</h3>
        <div className="totals">
          <div><span>Win </span><strong>{summary.win}</strong></div>
          <div><span>Loss </span><strong>{summary.loss}</strong></div>
          <div><span>Net </span><strong>{summary.net}</strong></div>
        </div>
      </div>

      <div className="ledger-panel">
        <div className="filters header-filter">
          <div className="field">
            <label>Type</label>
            <select name="type" value={filters.type} onChange={handleFilterChange}>
              <option value="all">All</option>
              <option value="win">Win</option>
              <option value="loss">Loss</option>
            </select>
          </div>
          <div className="field">
            <label>From</label>
            <input type="date" name="from" value={filters.from} onChange={handleFilterChange} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" name="to" value={filters.to} onChange={handleFilterChange} />
          </div>
        </div>
        <h3>History</h3>
        {loading ? (
          <Loader />
        ) : entries.length === 0 ? (
          <p className="subtext">No entries yet.</p>
        ) : (
          <div className="ledger-table-wrap">
            <table className="ledger-table">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e._id || e.id}>
                    <td>{e.entryDate || e.date}</td>
                    <td className={e.type === 'win' ? 'win' : 'loss'}>{e.type}</td>
                    <td className={e.type === 'win' ? 'win' : 'loss'}>{e.amount}</td>
                    <td>{e.note}</td>
                    <td className="actions">
                      <button className="icon-btn" onClick={() => startEdit(e)}>Edit</button>
                      <button className="icon-btn" onClick={() => removeEntry(e)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <ConfirmModal
        open={!!deleteTarget}
        title="Delete entry?"
        message="This action will permanently delete this ledger entry."
        confirmText="Delete"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}
